import { Col, Divider, Form, Input, Radio, Row, Space, Typography } from "antd";
import { VuiButton, VuiDatePicker } from "../../../../@vendor/components";
import { DEFAULT_PHONE_COUNTRY_CODE } from "../../../../constants";
import PhoneInput from "../../../../@vendor/components/atoms/PhoneInput";
import { useBookingCounseling } from "../../providers/booking-counseling.provider";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Patient } from "../../../../models/Patient";
import AuthRepository from "../../../../repositories/AuthRepository";
import { isValidEmail } from "../../../../@vendor/utils";
import { AxiosError } from "axios";
import _ from "lodash";
import { CountryPhoneInputValue } from "antd-country-phone-input";
import { useDebounce, useWindowSize } from "usehooks-ts";
import { IFormCustomValidation } from "../../../../@vendor/utils/interfaces/form.interface";

const BookingCounselingGuestPatient = () => {
  const { t } = useTranslation();
  const { form, changePatientPhoneValid, changePatientEmailValid } =
    useBookingCounseling();
  const navigate = useNavigate();
  const { width } = useWindowSize();
  const isMobile = width < 768;
  const { values, setFieldValue } = form;
  const [continueAsGuest, setContinueAsGuest] = useState<boolean>(false);
  const [formGuest] = Form.useForm<Patient>();
  const debounceEmail = useDebounce<string>(values?.patient?.email ?? "", 500);
  const phone = Form.useWatch(
    ["patient", "phone_number"],
    formGuest
  ) as CountryPhoneInputValue;
  const debouncePhone = useDebounce<CountryPhoneInputValue>(phone, 500);
  const [emailValidation, setEmailValidation] = useState<IFormCustomValidation>(
    {
      validateStatus: undefined,
      errorMessage: undefined,
    }
  );
  const [phoneValidation, setPhoneValidation] = useState<IFormCustomValidation>(
    {
      validateStatus: undefined,
      errorMessage: undefined,
    }
  );
  const handleContinueAsGuest = () => setContinueAsGuest(true);
  const handleLoginOrSignup = () => {
    localStorage.setItem("redirectToBooking", "counseling");
    navigate("/auth/login");
  };

  const handleGuestFormValuesChange = (changedValues: any) => {
    if (changedValues.patient) {
      setFieldValue("patient", {
        ...values.patient,
        ...changedValues.patient,
      });
    }
  };

  const isValidPhone = useCallback((phoneValue: CountryPhoneInputValue) => {
    const phoneLength = _.get(phoneValue, "phone.length", 0);

    return phoneLength >= 7 && phoneLength <= 15;
  }, []);

  const handleValidateEmail = useCallback(async () => {
    setEmailValidation((s) => ({
      ...s,
      validateStatus: "validating",
      errorMessage: undefined,
    }));
    changePatientEmailValid(true);

    await AuthRepository.checkEmail({
      email: values?.patient?.email ?? "",
    })
      .then(() => {
        if (isValidEmail(values?.patient?.email ?? "")) {
          setEmailValidation((s) => ({
            ...s,
            validateStatus: "success",
          }));
        } else {
          setEmailValidation((s) => ({
            ...s,
            validateStatus: undefined,
            errorMessage: undefined,
          }));
        }
      })
      .catch((error: AxiosError) => {
        if (error.name !== "CanceledError") {
          changePatientEmailValid(false);
          setEmailValidation((s) => ({
            ...s,
            validateStatus: "error",
            errorMessage: t("common.text.emailAlreadyUsed"),
          }));
        }
      });
  }, [values?.patient?.email]);

  const handleValidatePhone = useCallback(async () => {
    setPhoneValidation((s) => ({
      ...s,
      validateStatus: "validating",
      errorMessage: undefined,
    }));
    changePatientPhoneValid(true);

    const phoneValue = formGuest.getFieldValue(["patient", "phone_number"]);
    const phoneCountry = phoneValue?.code || "";
    const phoneNumber = phoneValue?.phone || "";

    await AuthRepository.checkPhone({
      phone_number: `${String(phoneCountry)} ${phoneNumber}`,
    })
      .then(() => {
        if (isValidPhone(phoneValue)) {
          setPhoneValidation((s) => ({
            ...s,
            validateStatus: "success",
          }));
        } else {
          setPhoneValidation((s) => ({
            ...s,
            validateStatus: undefined,
            errorMessage: undefined,
          }));
        }
      })
      .catch((error) => {
        if (error.name !== "CanceledError") {
          changePatientPhoneValid(false);
          setPhoneValidation((s) => ({
            ...s,
            validateStatus: "error",
            errorMessage: t("common.text.phoneAlreadyUsed"),
          }));
        }
      });
  }, [formGuest, isValidPhone]);

  useEffect(() => {
    if (isValidEmail(debounceEmail)) {
      handleValidateEmail().then();
    } else {
      changePatientEmailValid(true);
      if (debounceEmail) {
        setEmailValidation((s) => ({
          ...s,
          validateStatus: "error",
          errorMessage: t("validation.type.email", {
            item: t("common.text.patientEmail"),
          }),
        }));
      } else {
        setEmailValidation((s) => ({
          ...s,
          validateStatus: undefined,
          errorMessage: undefined,
        }));
      }
    }
  }, [debounceEmail]);

  useEffect(() => {
    if (isValidPhone(debouncePhone)) {
      handleValidatePhone().then();
    } else {
      changePatientPhoneValid(true);
      if (debouncePhone?.phone) {
        setPhoneValidation((s) => ({
          ...s,
          validateStatus: "error",
          errorMessage: t("validation.invalid", {
            item: t("common.text.patientPhoneNumber"),
          }),
        }));
      } else {
        setPhoneValidation((s) => ({
          ...s,
          validateStatus: undefined,
          errorMessage: undefined,
        }));
      }
    }
  }, [debouncePhone]);

  return (
    <Row gutter={[15, 15]} align="middle">
      <Col xs={24}>
        <div className="schedule-section-title" style={{ marginBottom: 0 }}>
          {t("common.text.patientForm")}
        </div>
      </Col>
      <Col xs={24}>
        <Space
          direction="vertical"
          size={20}
          align="center"
          style={{ width: "100%" }}
        >
          <Row align="middle">
            <Col xs={24} style={{ textAlign: "center" }}>
              <span className="basp-muted">
                {t("common.text.continueAsAGuestToFillInYourDetails")}
              </span>
            </Col>
          </Row>
          <Space>
            <VuiButton
              buttonProps={{
                type: "primary",
                className: "vui-button primary-outline",
                onClick: handleContinueAsGuest,
                size: isMobile ? "small" : "middle",
              }}
              label={t("common.button.continueAsGuest")}
            />
            <VuiButton
              buttonProps={{
                type: "primary",
                className: "vui-button primary",
                onClick: handleLoginOrSignup,
                size: isMobile ? "small" : "middle",
              }}
              label={t("common.button.logInOrSignup")}
            />
          </Space>
        </Space>
      </Col>

      {continueAsGuest && (
        <>
          <Divider style={{ marginTop: 30, marginBottom: 20 }} />
          <Col xs={24}>
            <Space direction="vertical" size={10} style={{ width: "100%" }}>
              <Row align="middle">
                <Col xs={24} style={{ textAlign: "center" }}>
                  <span className="basp-muted">
                    {t("common.text.kindlyFillOutTheFormBelow")}
                  </span>
                </Col>
              </Row>
              <Form
                form={formGuest}
                layout="vertical"
                onValuesChange={handleGuestFormValuesChange}
              >
                <Row gutter={[20, 0]}>
                  <Col xs={24}>
                    <Form.Item
                      name={["patient", "name"]}
                      label={t("common.text.patientName")}
                    >
                      <Input
                        placeholder={t("common.form.fullName.placeholder")}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12}>
                    <Form.Item
                      name={["patient", "gender"]}
                      label={t("common.text.patientGender")}
                    >
                      <Radio.Group>
                        <Radio value="Laki-laki">{t("common.text.male")}</Radio>
                        <Radio value="Perempuan">
                          {t("common.text.female")}
                        </Radio>
                      </Radio.Group>
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12}>
                    <Form.Item
                      name={["patient", "date_of_birth"]}
                      label={t("common.text.patientDateOfBirth")}
                    >
                      <VuiDatePicker />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12}>
                    <Form.Item
                      validateStatus={emailValidation.validateStatus}
                      help={emailValidation.errorMessage}
                      name={["patient", "email"]}
                      label={t("common.text.patientEmail")}
                    >
                      <Input placeholder={t("common.form.email.placeholder")} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12}>
                    <Form.Item
                      initialValue={{
                        code: DEFAULT_PHONE_COUNTRY_CODE,
                      }}
                      name={["patient", "phone_number"]}
                      label={t("common.text.patientPhoneNumber")}
                      validateStatus={phoneValidation.validateStatus}
                      help={phoneValidation.errorMessage}
                    >
                      <PhoneInput
                        placeholder={t("common.form.phone.placeholder")}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </Space>
            <Typography.Paragraph
              style={{ color: "#C6C8CC", fontSize: 14, marginTop: -12 }}
            >
              *{t("common.text.emailPhoneDisclaimer")}
            </Typography.Paragraph>
          </Col>
        </>
      )}
    </Row>
  );
};

export default BookingCounselingGuestPatient;
